import { ButtonGroup, Flex, Grid, GridItem, Heading, Skeleton } from "@chakra-ui/react";
import type React from "react";
import { useMemo } from "react";

import { ButtonLink } from "@equidefi/portals/components/ButtonLink";
import { Icon } from "@equidefi/ui/icon";

import { OfferingExemptionBadge } from "@/areas/Offerings/components/OfferingExemptionBadge";
import { HeaderAvatar, HeaderBreadcrumb, HeaderCard } from "@/components/nav/HeaderCard";
import { ShareUrlButton } from "@/components/offerings/ShareUrlButton";
import { useCanEdit } from "@/components/permissions/Can";
import { CONFIG } from "@/constants/config";

interface OfferingHeaderCardProps {
  offering: any;
}

export function OfferingHeaderCard({ offering }: OfferingHeaderCardProps): React.JSX.Element {
  const { issuer, name, exemption_type, id: offeringId } = offering || {};
  const { name: issuerName, photo_url: issuerPhotoUrl } = issuer || {};

  const canAccessSettings = useCanEdit({ userRole: "owner", offeringId, issuerId: offering?.issuer_id });

  const shareUrl = useMemo(() => {
    if (offering?.slug) {
      return new URL(`/offerings/${offering.slug}/registration`, CONFIG.EQ_APP_URL);
    }
  }, [offering?.slug]);

  return (
    <HeaderCard>
      <Flex direction={{ base: "column", md: "row" }} gap={{ base: 5, md: 0 }} justify="space-between" align="center">
        <Grid templateRows="auto auto" templateColumns="repeat(8, 1fr)" gap={2} minH="70px">
          <GridItem w="80px" alignItems="center" rowSpan={2}>
            <HeaderAvatar url={issuerPhotoUrl} alt={`${issuerName} Logo`} boxSize="70px" />
          </GridItem>
          <GridItem alignContent="center" colSpan={6}>
            <HeaderBreadcrumb crumbs={issuerName ? { [issuerName]: `/issuers/${offering?.issuer_id}` } : {}} />
          </GridItem>
          <GridItem alignContent="center" colSpan={6}>
            <Flex align="flex-start" gap={3} flexWrap="wrap">
              {name ? (
                <Heading as="h1" fontSize={{ base: "xl", md: "2xl" }} lineHeight={1.2} mb={0} mt={-1}>
                  {name}
                </Heading>
              ) : (
                <Skeleton mt="-1.5em" h="1.7em" w="30em" />
              )}
              <OfferingExemptionBadge mt={"0.5"} type={exemption_type} />
            </Flex>
          </GridItem>
        </Grid>
        <ButtonGroup variant="ghost" colorScheme="gray" size="sm" gap={1}>
          <ShareUrlButton
            leftIcon={<Icon.Clipboard size="1.4em" />}
            p="5"
            isDisabled={!offering?.slug}
            content={String(shareUrl)}
            tooltip={offering?.slug ? "" : "No slug configured yet"}>
            Share URL
          </ShareUrlButton>

          {canAccessSettings && (
            <ButtonLink to={`/offerings/${offeringId}/admin/details`} p="5" leftIcon={<Icon.Settings size="1.4em" />}>
              Offering Settings
            </ButtonLink>
          )}
        </ButtonGroup>
      </Flex>
    </HeaderCard>
  );
}
